import type {IGallerySantaProps, IPropsInjectedByViewerScript} from '../types/galleryTypes';
import {IHostProps} from '@wix/native-components-infra/dist/es/src/types/types';
import {IStylesContextValue} from '@wix/tpa-settings';
import {IStylesParams} from '../styleParams/types';
import {ReadOnlyExperiments} from '@wix/wix-experiments/dist/src/ReadOnlyExperiments';

type ProvidedGlobalPropNames =
  | 'allowFreeProducts'
  | 'addedToCartStatus'
  | 'clearFilters'
  | 'currentPage'
  | 'useCategories'
  | 'useSidePanel'
  | 'scrollToProduct'
  | 'totalPages'
  | 'maxProductsPerPage'
  | 'linkForAllPages'
  | 'nextPrevLinks'
  | 'experiments'
  | 'filterModels'
  | 'filterProducts'
  | 'applyFilteredProductsOnMobile'
  | 'focusedProductIndex'
  | 'getCategoryProducts'
  | 'handleAddToCart'
  | 'closeQuickAdd'
  | 'sendSortClickBiEvent'
  | 'sendClickShippingInfoLinkSf'
  | 'handleCategoryBreadcrumbsClick'
  | 'handleCategoryClampClick'
  | 'handlePagination'
  | 'reportProductItemClick'
  | 'handleProductItemClick'
  | 'handleProductsOptionsChange'
  | 'hasMoreProductsToLoad'
  | 'hasSelectedFilters'
  | 'htmlTags'
  | 'host'
  | 'imagePosition'
  | 'imageMode'
  | 'imageRatio'
  | 'isAutoGrid'
  | 'isCategoryPage'
  | 'isFirstPage'
  | 'isHorizontalLayout'
  | 'isInteractive'
  | 'isLiveSiteMode'
  | 'isPreviewMode'
  | 'isGalleryRowsAndColsWereModified'
  | 'shouldShowMobile'
  | 'isCategoryVisible'
  | 'isEditorMode'
  | 'isRTL'
  | 'isSEO'
  | 'isSSR'
  | 'isMobileOnDesktopMode'
  | 'isCatalogV3'
  | 'loadMoreProducts'
  | 'loadMoreType'
  | 'mainCollectionId'
  | 'numberOfSelectedFilterTypes'
  | 'openQuickView'
  | 'paginationMode'
  | 'priceBreakdown'
  | 'productsManifest'
  | 'products'
  | 'productsVariantInfoMap'
  | 'productsPriceRangeMap'
  | 'selectedSort'
  | 'shouldAlternateImagePosition'
  | 'shouldShowAddToCartSuccessAnimation'
  | 'shouldShowClearFilters'
  | 'shouldShowMobileFiltersModal'
  | 'hasActiveSortOption'
  | 'shouldShowImageCarousel'
  | 'shouldShowProductOptions'
  | 'shouldUseAutoGridProductsCount'
  | 'showShowLightEmptyState'
  | 'sortProducts'
  | 'sortingOptions'
  | 'textsMap'
  | 'toggleFiltersModalVisibility'
  | 'totalProducts'
  | 'updateAddToCartStatus'
  | 'usingExtendedGridGalleryStyles'
  | 'useFiltersAnnouncer'
  | 'productsRequestInProgress'
  | 'currentCategory'
  | 'categories'
  | 'slots'
  | 'breadcrumbsHistory'
  | 'taxonomicalBreadcrumbs'
  | 'isOptionsRevealEnabled'
  | 'hasFilters'
  | 'navigateToFirstPage'
  | 'allProductsCategoryId';

export type IGlobals = Pick<IPropsInjectedByViewerScript & IGallerySantaProps, ProvidedGlobalPropNames> & {
  styles: IStylesContextValue;
  stylesParams: IStylesParams;
  useExperiments: ReadOnlyExperiments;
} & Pick<IHostProps, 'updateLayout' | 'dimensions' | 'appLoadBI' | 'accessibilityEnabled'> & {compId: string};

export interface IGalleryGlobalProps {
  globals?: IGlobals;
}

export const galleryGlobalsStrategy = (props: IPropsInjectedByViewerScript & IGallerySantaProps) => {
  const {
    allowFreeProducts,
    addedToCartStatus,
    clearFilters,
    currentPage,
    useCategories,
    useSidePanel,
    scrollToProduct,
    totalPages,
    maxProductsPerPage,
    linkForAllPages,
    nextPrevLinks,
    experiments,
    filterModels,
    filterProducts,
    applyFilteredProductsOnMobile,
    focusedProductIndex,
    getCategoryProducts,
    handleAddToCart,
    closeQuickAdd,
    sendClickShippingInfoLinkSf,
    sendSortClickBiEvent,
    handleCategoryBreadcrumbsClick,
    handleCategoryClampClick,
    handlePagination,
    reportProductItemClick,
    handleProductItemClick,
    handleProductsOptionsChange,
    hasMoreProductsToLoad,
    hasSelectedFilters,
    htmlTags,
    imagePosition,
    imageMode,
    imageRatio,
    isAutoGrid,
    isCategoryPage,
    isFirstPage,
    isHorizontalLayout,
    isInteractive,
    isLiveSiteMode,
    isPreviewMode,
    shouldShowMobile,
    isCategoryVisible,
    isEditorMode,
    isRTL,
    isSEO,
    isSSR,
    isMobileOnDesktopMode,
    isCatalogV3,
    isGalleryRowsAndColsWereModified,
    loadMoreProducts,
    loadMoreType,
    mainCollectionId,
    numberOfSelectedFilterTypes,
    openQuickView,
    paginationMode,
    priceBreakdown,
    productsManifest,
    products,
    productsVariantInfoMap,
    productsPriceRangeMap,
    selectedSort,
    shouldAlternateImagePosition,
    shouldShowAddToCartSuccessAnimation,
    shouldShowClearFilters,
    shouldShowMobileFiltersModal,
    shouldUseAutoGridProductsCount,
    hasActiveSortOption,
    showShowLightEmptyState,
    shouldShowImageCarousel,
    shouldShowProductOptions,
    sortProducts,
    sortingOptions,
    textsMap,
    toggleFiltersModalVisibility,
    totalProducts,
    updateAddToCartStatus,
    usingExtendedGridGalleryStyles,
    useFiltersAnnouncer,
    productsRequestInProgress,
    isOptionsRevealEnabled,
    currentCategory,
    breadcrumbsHistory,
    taxonomicalBreadcrumbs,
    categories,
    hasFilters,
    slots,
    navigateToFirstPage,
    ...locals
  } = props;

  const {appLoadBI, dimensions, updateLayout, id: compId, accessibilityEnabled} = props.host;
  return {
    globals: {
      accessibilityEnabled,
      allowFreeProducts,
      addedToCartStatus,
      appLoadBI,
      clearFilters,
      currentPage,
      useCategories,
      useSidePanel,
      scrollToProduct,
      totalPages,
      maxProductsPerPage,
      linkForAllPages,
      nextPrevLinks,
      compId,
      dimensions,
      experiments,
      filterModels,
      filterProducts,
      applyFilteredProductsOnMobile,
      focusedProductIndex,
      getCategoryProducts,
      handleAddToCart,
      closeQuickAdd,
      handleCategoryBreadcrumbsClick,
      handleCategoryClampClick,
      handlePagination,
      reportProductItemClick,
      handleProductItemClick,
      handleProductsOptionsChange,
      hasMoreProductsToLoad,
      hasSelectedFilters,
      host: props.host,
      htmlTags,
      imagePosition,
      imageMode,
      imageRatio,
      isAutoGrid,
      isCategoryPage,
      isFirstPage,
      isHorizontalLayout,
      isInteractive,
      isLiveSiteMode,
      isPreviewMode,
      shouldShowMobile,
      isCategoryVisible,
      isEditorMode,
      isRTL,
      isSEO,
      isSSR,
      isMobileOnDesktopMode,
      isCatalogV3,
      isGalleryRowsAndColsWereModified,
      loadMoreProducts,
      loadMoreType,
      mainCollectionId,
      numberOfSelectedFilterTypes,
      openQuickView,
      paginationMode,
      priceBreakdown,
      productsManifest,
      products,
      productsVariantInfoMap,
      productsPriceRangeMap,
      selectedSort,
      sendSortClickBiEvent,
      sendClickShippingInfoLinkSf,
      shouldAlternateImagePosition,
      shouldShowAddToCartSuccessAnimation,
      shouldShowClearFilters,
      shouldShowMobileFiltersModal,
      hasActiveSortOption,
      shouldUseAutoGridProductsCount,
      showShowLightEmptyState,
      shouldShowImageCarousel,
      shouldShowProductOptions,
      useFiltersAnnouncer,
      sortProducts,
      sortingOptions,
      slots,
      textsMap,
      toggleFiltersModalVisibility,
      totalProducts,
      updateLayout,
      updateAddToCartStatus,
      usingExtendedGridGalleryStyles,
      productsRequestInProgress,
      isOptionsRevealEnabled,
      currentCategory,
      breadcrumbsHistory,
      taxonomicalBreadcrumbs,
      categories,
      hasFilters,
      navigateToFirstPage,
    },
    locals,
  };
};
